import React, { useMemo } from 'react';
import { Document, Font, Image, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import moment from 'moment';
import Logo from "../../../../images/logo.png";
import LatoRegular from "../../../../utils/fonts/Lato-Regular.ttf";
import LatoBold from '../../../../utils/fonts/Lato-Bold.ttf';
import { formatNumber } from '../../../../utils/functionalUtils';
import { formatWeighbridgeWeightMt, resolveWeighbridgeNetWeight } from '../../../../utils/weighbridgeUtils';

// Register fonts
Font.register({
    family: 'Lato',
    fonts: [
        { src: LatoRegular },
        { src: LatoBold, fontWeight: 'bold' }
    ]
});

// Define styles
const styles = StyleSheet.create({
    page: {
        padding: 15,
        fontFamily: 'Lato',
        fontSize: 10,
        color: '#333',
        backgroundColor: '#fff'
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 5,
        paddingBottom: 5
    },
    logoContainer: {
        width: 100
    },
    addressText: {
        fontSize: 8,
        color: '#555'
    },
    deliveryNoteBadge: {
        backgroundColor: '#2c3782',
        padding: 10,
        color: 'white',
        borderRadius: 4,
        fontSize: 16,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 10
    },
    detailContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 10
    },
    detailColumn: {
        flexBasis: '48%',
        borderWidth: 1,
        borderColor: '#ddd',
        padding: 5,
        marginBottom: 5,
        margin: "auto"
    },
    detailRow: {
        flexDirection: 'row',
        marginVertical: 3
    },
    detailRowOther: {
        flexDirection: 'column',
        marginVertical: 3,
        width: '50%'
    },
    label: {
        fontWeight: 'bold',
    },
    sectionTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#2c3782',
        marginBottom: 5,
        textAlign: 'center',
        textTransform: 'uppercase'
    },
    tableContainer: {
        marginVertical: 5
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#2c3782',
        color: 'white',
        textAlign: 'center',
        fontSize: 10,
        fontWeight: 'bold'
    },
    tableHeaderCell: {
        padding: 3,
        border: '1px solid #fff',
        textAlign: 'center'
    },
    tableRow: {
        flexDirection: 'row',
        fontSize: 8
    },
    tableCell: {
        padding: 3,
        textAlign: 'center',
        border: '1px solid #526484',
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginTop: 10,
        paddingTop: 5,
        borderTop: '2px solid #2c3782'
    },
    totalLabel: {
        width: '70%',
        textAlign: 'right',
        paddingRight: 10,
        fontWeight: 'bold'
    },
    totalValue: {
        width: '15%',
        textAlign: 'center',
        fontWeight: 'bold',
        borderBottom: '1px solid #ddd'
    },
    signatureSection: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 20
    },
    signatureBlock: {
        width: '48%',
        border: '1px solid #ddd',
        padding: 10
    },
    signatureTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 5,
        textTransform: 'uppercase'
    },
    signatureField: {
        marginTop: 30,
        borderTop: '1px solid #333',
        paddingTop: 4
    },
    stampContainer: {
        width: '100%',
        height: 100,
        border: '2px solid #333',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 5
    },
    stampDate: {
        fontSize: 9,
        fontWeight: 'bold',
        textAlign: 'center'
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 30,
        right: 30,
        fontSize: 8,
        color: '#666',
        textAlign: 'center',
        borderTop: '1px solid #e0e0e0',
        paddingTop: 8
    },
    receivingContainer: {
        padding: 5
    },
    receivingWrapper: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: '1px solid #C0C0C0',
        borderRadius: 1,
        marginTop: 10
    },
    receivingRow: {
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    customerDetailsColumn: {
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: '50%',
        borderRight: '2px solid #A9A9A9'
    },
    customerDetailsHeader: {
        borderBottom: '1px solid #A9A9A9'
    },
    customerDetailsTitle: {
        textAlign: 'center',
        textTransform: 'uppercase',
        fontSize: 12,
        fontWeight: 'bold',
        padding: 1
    },
    detailItem: {
        flexDirection: 'row',
        borderBottom: '1px solid #A9A9A9',
        fontSize: 12
    },
    detailLabel: {
        paddingLeft: 5,
        width: '40%',
        borderRight: '1px solid #A9A9A9',
        fontWeight: 'bold'
    },
    detailValue: {
        paddingLeft: 5
    },
    stampColumn: {
        flexDirection: 'column',
        width: '18%',
        borderRight: '2px solid #A9A9A9'
    },
    stampHeader: {
        borderBottom: '1px solid #A9A9A9'
    },
    stampTitle: {
        textAlign: 'center',
        textTransform: 'uppercase',
        fontSize: 12,
        fontWeight: 'bold',
        padding: 1
    },
    stampBox: {
        width: '95%',
        height: 70,
        margin: 'auto',
        border: '2px solid black',
        alignSelf: 'center',
        alignItems: 'center',
        justifyContent: 'center'
    },
    stampLogoContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 2
    },
    stampLogo: {
        width: 20,
        height: 20,
        marginRight: 2
    },
    stampLogoText: {
        fontSize: 9,
        fontWeight: 'bold'
    },
    stampInnerBox: {
        width: '75%',
        height: 35,
        border: '2px solid black',
        padding: 1
    },
    stampDateText: {
        fontSize: 9,
        fontWeight: 'bold',
        textAlign: 'center'
    },
    stampConfirmer: {
        height: 15
    },
    goodsReceivedColumn: {
        flexDirection: 'column',
        justifyContent: 'flex-start',
        width: '32%',
        padding: 2,
        borderRight: '2px solid #A9A9A9'
    },
    goodsReceivedTitle: {
        textAlign: 'center',
        textTransform: 'uppercase',
        fontSize: 12,
        fontWeight: 'bold',
        padding: 1,
        borderBottom: '1px solid #A9A9A9'
    },
    discrepancyNotice: {
        textAlign: 'center',
        textTransform: 'uppercase',
        fontSize: 8,
        fontWeight: 'bold',
        padding: 1
    },
    pageBreak: {
        marginTop: 20,
        borderTop: '1px dashed #ccc',
        paddingTop: 10
    }
});

const DeliveryNotePDF = ({ page_data }) => {
    const pdf_data = page_data || {};

    const totals = useMemo(() => {
        const productList = pdf_data?.product_list || [];

        const totalCapacitySum = productList.reduce((sum, item) => {
            return sum + parseFloat(item?.total_capacity_in_mt || 0);
        }, 0);

        const totalDensityCapacitySum = productList.reduce((sum, item) => {
            return sum + parseFloat(item?.uom_details?.total_density_kilos || 0);
        }, 0);

        return {
            total_mt_cap: totalCapacitySum.toFixed(2),
            total_density_capacity: totalDensityCapacitySum,
            total_base_capacity: totalCapacitySum,
        };
    }, [pdf_data]);

    const wb = useMemo(() => {
        const backendTicket = pdf_data?.weigh_bridge_ticket || null;
        const selectorTicket =
            pdf_data?.weighbridge_ticket_data ||
            pdf_data?.weighBridgeTicket ||
            null;

        if (backendTicket) {
            return {
                source: "backend",
                ticket_number:
                    backendTicket?.weigh_bridge_ticket_reference_no ||
                    backendTicket?.ticket_number ||
                    "---",
                vehicle_number:
                    backendTicket?.truck_licence_plate ||
                    backendTicket?.vehicle_number ||
                    "---",
                first_weight_mt: backendTicket?.first_weight ?? null,
                second_weight_mt: backendTicket?.second_weight ?? null,
                net_weight_mt: resolveWeighbridgeNetWeight(
                    backendTicket?.net_weight ?? null,
                    backendTicket?.first_weight ?? null,
                    backendTicket?.second_weight ?? null,
                ),
                net_weight_kg: null,
                weighed_at: backendTicket?.weighed_at || backendTicket?.created_at || null,
                comments: backendTicket?.comments || "---",
                trailer_one: backendTicket?.trailer_one || "--",
                trailer_two: backendTicket?.trailer_two || "--",
                transporter_name: backendTicket?.transporter_name || "--",
                truck_driver_name: backendTicket?.truck_driver_name || "--",
            };
        }

        if (selectorTicket) {
            return {
                source: "selector",
                ticket_number: selectorTicket?.ticket_number || "---",
                vehicle_number: selectorTicket?.vehicle_number || "---",
                first_weight_mt: selectorTicket?.first_weight ?? null,
                second_weight_mt: selectorTicket?.second_weight ?? null,
                net_weight_mt: resolveWeighbridgeNetWeight(
                    selectorTicket?.net_weight ?? null,
                    selectorTicket?.first_weight ?? null,
                    selectorTicket?.second_weight ?? null,
                ),
                net_weight_kg: null,
                weighed_at:
                    selectorTicket?.weighed_at ||
                    selectorTicket?.time_out ||
                    selectorTicket?.time_in ||
                    selectorTicket?.created_on ||
                    null,
                comments: selectorTicket?.comments || "---",
                trailer_one: selectorTicket?.trailer_one || "--",
                trailer_two: selectorTicket?.trailer_two || "--",
                transporter_name: selectorTicket?.transporter_name || "--",
                truck_driver_name: selectorTicket?.truck_driver_name || "--",
            };
        }

        return null;
    }, [pdf_data]);

    const needsPageBreak = (productCount) => {
        const estimatedContentHeight = productCount * 15 + 200;
        return estimatedContentHeight > 400;
    };

    const fmtMt = (val) => {
        return formatWeighbridgeWeightMt(val);
    };

    const fmtKg = (val) => {
        if (val === null || val === undefined || val === "") return "---";
        const num = parseFloat(val);
        if (!Number.isFinite(num)) return "---";
        return `${formatNumber(Number(num.toFixed(2)))} KG`;
    };

    const renderWeighbridgeTable = () => (
        <View style={{ marginBottom: 10, marginTop: needsPageBreak(pdf_data?.product_list?.length || 0) ? 20 : 0 }}>
            <Text style={styles.sectionTitle}>Weigh Bridge Weight</Text>

            <View style={styles.tableHeader}>
                <Text style={[styles.tableCell, { width: '18%' }]}>Ticket #</Text>
                <Text style={[styles.tableCell, { width: '18%' }]}>Vehicle</Text>
                <Text style={[styles.tableCell, { width: '16%' }]}>Tare (MT)</Text>
                <Text style={[styles.tableCell, { width: '16%' }]}>Gross (MT)</Text>
                <Text style={[styles.tableCell, { width: '16%' }]}>Net</Text>
                <Text style={[styles.tableCell, { width: '16%' }]}>Date</Text>
            </View>

            <View style={styles.tableRow}>
                <Text style={[styles.tableCell, { width: '18%' }]}>
                    {wb?.ticket_number || "---"}
                </Text>
                <Text style={[styles.tableCell, { width: '18%' }]}>
                    {wb?.vehicle_number || "---"}
                </Text>
                <Text style={[styles.tableCell, { width: '16%' }]}>
                    {fmtMt(wb?.first_weight_mt)}
                </Text>
                <Text style={[styles.tableCell, { width: '16%' }]}>
                    {fmtMt(wb?.second_weight_mt)}
                </Text>
                <Text style={[styles.tableCell, { width: '16%' }]}>
                    {fmtMt(wb?.net_weight_mt)}
                    {wb?.net_weight_kg ? ` (${fmtKg(wb.net_weight_kg)})` : ""}
                </Text>
                <Text style={[styles.tableCell, { width: '16%' }]}>
                    {wb?.weighed_at ? moment(wb.weighed_at).format("Do MMM, YYYY HH:mm") : "---"}
                </Text>
            </View>

            <View style={styles.tableRow}>
                <Text style={[styles.tableCell, { width: '18%' }]}></Text>
                <Text style={[styles.tableCell, { width: '18%' }]}></Text>
                <Text style={[styles.tableCell, { width: '64%' }]}>
                    <Text style={styles.label}>Comments: </Text>
                    <Text>{wb?.comments || "---"}</Text>
                </Text>
            </View>
        </View>
    );

    const renderSignatureSection = () => (
        <View style={styles.receivingContainer}>
            <View style={styles.receivingWrapper}>
                <View style={styles.receivingRow}>
                    <View style={styles.customerDetailsColumn}>
                        <View style={styles.customerDetailsHeader}>
                            <Text style={styles.customerDetailsTitle}>Customer Receiving Details</Text>
                        </View>
                        <View>
                            <View style={styles.detailItem}>
                                <View style={styles.detailLabel}>
                                    <Text>Receiver Name</Text>
                                </View>
                                <View style={styles.detailValue}>
                                    <Text>{pdf_data?.delivery_note?.customer_receiving_name}</Text>
                                </View>
                            </View>
                            <View style={styles.detailItem}>
                                <View style={styles.detailLabel}>
                                    <Text>Receiver Signature</Text>
                                </View>
                                <View style={styles.detailValue}>
                                    <Text></Text>
                                </View>
                            </View>
                            <View style={styles.detailItem}>
                                <View style={styles.detailLabel}>
                                    <Text>Receiver Contact Details</Text>
                                </View>
                                <View style={styles.detailValue}>
                                    <Text>{pdf_data?.delivery_note?.customer_receiving_primary_mobile}</Text>
                                </View>
                            </View>
                            <View style={[styles.detailItem, { borderBottomWidth: 0 }]}>
                                <View style={styles.detailLabel}>
                                    <Text>Date Received:</Text>
                                </View>
                                <View style={styles.detailValue}>
                                    <Text>
                                        {pdf_data?.delivery_note?.delivery_date_time
                                            ? moment(pdf_data?.delivery_note?.delivery_date_time).format("Do MMMM , YYYY h:mm:ss")
                                            : "---"}
                                    </Text>
                                </View>
                            </View>
                        </View>
                    </View>

                    <View style={styles.stampColumn}>
                        <View style={styles.stampHeader}>
                            <Text style={styles.stampTitle}>Customer Stamp</Text>
                        </View>
                        {pdf_data?.delivery_note?.delivery_date_time && (
                            <View style={styles.stampBox}>
                                <View style={styles.stampLogoContainer}>
                                    <Image src={Logo} style={styles.stampLogo} />
                                    <Text style={styles.stampLogoText}>United African Grain</Text>
                                </View>
                                <View style={styles.stampInnerBox}>
                                    <Text style={styles.stampDateText}>
                                        {moment(pdf_data?.delivery_note?.delivery_date_time).format("Do MMMM , YYYY")}
                                    </Text>
                                    <Text style={styles.stampDateText}>
                                        {moment(pdf_data?.delivery_note?.delivery_date_time).format("h:mm:ss")}
                                    </Text>
                                </View>
                                <View style={styles.stampConfirmer}>
                                    <Text style={styles.stampDateText}>
                                        {pdf_data?.delivery_note?.user_delivery_confirmer || "---"}
                                    </Text>
                                </View>
                            </View>
                        )}
                    </View>

                    <View style={styles.goodsReceivedColumn}>
                        <View>
                            <Text style={styles.goodsReceivedTitle}>Goods Received in Good Order</Text>
                        </View>
                        <View style={{ padding: 2 }}>
                            <Text style={styles.discrepancyNotice}>
                                Please Indicate any Delivery Shortage or Discrepancy
                            </Text>
                        </View>
                    </View>
                </View>
            </View>
        </View>
    );

    return (
        <Document title={`Delivery Note - ${pdf_data?.delivery_note?.delivery_note_ref_number || "---"}`}>
            <Page size="A4" orientation="landscape" style={styles.page}>
                <View style={styles.header} fixed>
                    <View style={{ flexDirection: 'row', alignItems: 'center', width: "50%" }}>
                        <View style={styles.logoContainer}>
                            <Image src={Logo} />
                        </View>
                        <View style={{ marginLeft: 10 }}>
                            <Text style={styles.addressText}>Plot 10111 off Mumbwa Road, Chinika Area</Text>
                            <Text style={styles.addressText}>P.O. Box 30203, Lusaka, Zambia.</Text>
                            <Text style={styles.addressText}>Mobile: +260 764 299 293</Text>
                            <Text style={styles.addressText}>Email: info@iagzambia.com</Text>
                        </View>
                    </View>

                    <View style={{ alignItems: 'flex-end', width: "50%" }}>
                        <Text style={styles.deliveryNoteBadge}>DELIVERY NOTE</Text>
                        <View style={{ marginTop: 5 }}>
                            <View style={styles.detailRow}>
                                <Text style={styles.label}>D.N:</Text>
                                <Text style={styles.value}>{pdf_data?.delivery_note?.delivery_note_ref_number || "---"}</Text>
                            </View>
                            <View style={styles.detailRow}>
                                <Text style={styles.label}>Branch Location:</Text>
                                <Text style={styles.value}>
                                    {pdf_data?.branch_location?.branch_area
                                        ? `${pdf_data?.branch_location?.branch_area}, ${pdf_data?.branch_location?.branch_physical_address}`
                                        : "Chinika Area Plot 10111 off Mumbwa Road"}
                                </Text>
                            </View>
                            <View style={styles.detailRow}>
                                <Text style={styles.label}>Date Dispatched:</Text>
                                <Text style={styles.value}>
                                    {pdf_data?.delivery_note?.transit_date_time
                                        ? moment(pdf_data?.delivery_note?.transit_date_time).format("MMMM Do, YYYY")
                                        : "---"}
                                </Text>
                            </View>
                        </View>
                    </View>
                </View>

                <View style={styles.detailContainer}>
                    <View style={[styles.detailColumn, { border: '1px solid #ddd', padding: 8 }]}>
                        <Text style={styles.sectionTitle}>Customer Details</Text>
                        <View style={{ display: "flex", flexDirection: "row" }}>
                            <View style={styles.detailRowOther}>
                                <Text style={[styles.label, { marginBottom: 1 }]}>Customer Name:</Text>
                                <Text style={styles.value}>{pdf_data?.consigner?.name || "--"}</Text>
                            </View>
                            <View style={styles.detailRowOther}>
                                <Text style={[styles.label, { marginBottom: 1 }]}>Delivery Location:</Text>
                                <Text style={styles.value}>{pdf_data?.delivery_note?.delivery_location || "--"}</Text>
                            </View>
                        </View>
                        <View style={styles.detailRowOther}>
                            <Text style={[styles.label, { marginBottom: 1 }]}>Contact Details:</Text>
                            <Text style={styles.value}>{pdf_data?.consigner?.mobile_number || "--"}</Text>
                        </View>
                        <View style={styles.detailRowOther}>
                            <Text style={[styles.label, { marginBottom: 1 }]}>Comments:</Text>
                            <Text style={styles.value}>{pdf_data?.weigh_bridge_ticket?.comments || wb?.comments || "--"}</Text>
                        </View>
                    </View>

                    <View style={[styles.detailColumn, { border: '1px solid #ddd', padding: 8 }]}>
                        <Text style={styles.sectionTitle}>Transporter Details</Text>
                        <View style={{ display: "flex", flexDirection: "row" }}>
                            <View style={styles.detailRowOther}>
                                <Text style={[styles.label, { marginBottom: 1 }]}>Truck Reg No.:</Text>
                                <Text style={styles.value}>{pdf_data?.weigh_bridge_ticket?.truck_licence_plate || wb?.vehicle_number || "--"}</Text>
                            </View>
                            <View style={styles.detailRowOther}>
                                <Text style={[styles.label, { marginBottom: 1 }]}>Trailer No. 1:</Text>
                                <Text style={styles.value}>{pdf_data?.weigh_bridge_ticket?.trailer_one || wb?.trailer_one || "--"}</Text>
                            </View>
                        </View>
                        <View style={{ display: "flex", flexDirection: "row" }}>
                            <View style={styles.detailRowOther}>
                                <Text style={[styles.label, { marginBottom: 1 }]}>Trailer No. 2:</Text>
                                <Text style={styles.value}>{pdf_data?.weigh_bridge_ticket?.trailer_two || wb?.trailer_two || "--"}</Text>
                            </View>
                            <View style={styles.detailRowOther}>
                                <Text style={[styles.label, { marginBottom: 1 }]}>Transporter Name:</Text>
                                <Text style={styles.value}>{pdf_data?.weigh_bridge_ticket?.transporter_name || wb?.transporter_name || "--"}</Text>
                            </View>
                        </View>
                        <View style={{ display: "flex", flexDirection: "row" }}>
                            <View style={styles.detailRowOther}>
                                <Text style={[styles.label, { marginBottom: 1 }]}>Driver Name:</Text>
                                <Text style={styles.value}>{pdf_data?.weigh_bridge_ticket?.truck_driver_name || wb?.truck_driver_name || "--"}</Text>
                            </View>
                            <View style={styles.detailRowOther}>
                                <Text style={[styles.label, { marginBottom: 1 }]}>Driver Contact:</Text>
                                <Text style={styles.value}>{pdf_data?.delivery_note?.customer_receiving_primary_mobile || "--"}</Text>
                            </View>
                        </View>
                    </View>
                </View>

                <View style={styles.tableContainer}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                        <Text style={styles.sectionTitle}>Product Details</Text>
                        <View style={{ flexDirection: 'row' }}>
                            <View style={{ marginRight: 15 }}>
                                <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Base Weight (MT):</Text>
                                <Text style={{ fontSize: 10 }}>
                                    {totals.total_base_capacity !== ""
                                        ? `${parseFloat(totals.total_base_capacity).toFixed(2)} MT`
                                        : "---"}
                                </Text>
                            </View>
                            <View>
                                <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Density Weight (MT):</Text>
                                <Text style={{ fontSize: 10 }}>
                                    {totals.total_density_capacity !== ""
                                        ? `${parseFloat(totals.total_density_capacity || 0).toFixed(2)} MT`
                                        : "---"}
                                </Text>
                            </View>
                        </View>
                    </View>

                    <View style={styles.tableHeader}>
                        <Text style={[styles.tableHeaderCell, { width: '16.67%' }]}>Product</Text>
                        <Text style={[styles.tableHeaderCell, { width: '16.67%' }]}>Packet Size</Text>
                        <Text style={[styles.tableHeaderCell, { width: '16.67%' }]}>Unit Weight (KGs)</Text>
                        <Text style={[styles.tableHeaderCell, { width: '16.67%' }]}>Packaging No.</Text>
                        <Text style={[styles.tableHeaderCell, { width: '16.67%' }]}>Total Weight</Text>
                        <Text style={[styles.tableHeaderCell, { width: '16.67%' }]}>Total MT</Text>
                    </View>

                    {pdf_data?.product_list?.map((product, index) => (
                        <View key={index} style={styles.tableRow}>
                            <Text style={[styles.tableCell, { width: '16.67%' }]}>
                                {product?.product_name || "---"}
                            </Text>
                            <Text style={[styles.tableCell, { width: '16.67%' }]}>
                                {product?.packet_size || "---"}
                            </Text>
                            <Text style={[styles.tableCell, { width: '16.67%' }]}>
                                {product?.uom_details?.measurement_in_kilos
                                    ? `${formatNumber(parseFloat(product?.uom_details?.measurement_in_kilos).toFixed(2))} ${product?.uom_details?.unit_measurement_abbrev || ""}`
                                    : "---"}
                            </Text>
                            <Text style={[styles.tableCell, { width: '16.67%' }]}>
                                {product?.total_capacity_selected_measurement
                                    ? formatNumber(parseFloat(product?.total_capacity_selected_measurement).toFixed(2))
                                    : "---"}
                            </Text>
                            <Text style={[styles.tableCell, { width: '16.67%' }]}>
                                {product?.uom_details?.total_kilo
                                    ? `${formatNumber(parseFloat(product?.uom_details?.total_kilo * 1000).toFixed(2))} ${product?.uom_details?.unit_measurement_abbrev || ""}`
                                    : "---"}
                            </Text>
                            <Text style={[styles.tableCell, { width: '16.67%' }]}>
                                {product?.total_capacity_in_mt
                                    ? formatNumber(parseFloat(product?.total_capacity_in_mt).toFixed(2))
                                    : "---"}
                            </Text>
                        </View>
                    ))}
                </View>

                <View style={styles.totalRow}>
                    <Text style={styles.totalLabel}>Total Quantity (MT):</Text>
                    <Text style={styles.totalValue}>{totals.total_mt_cap}</Text>
                </View>

                {needsPageBreak(pdf_data?.product_list?.length || 0) ? (
                    <View style={styles.pageBreak}>
                        <Text>Continued on next page...</Text>
                    </View>
                ) : (
                    <>
                        {wb ? renderWeighbridgeTable() : null}
                        {renderSignatureSection()}
                    </>
                )}
            </Page>

            {needsPageBreak(pdf_data?.product_list?.length || 0) && (
                <Page size="A4" orientation="landscape" style={styles.page}>
                    <View style={styles.header}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', width: "50%" }}>
                            <View style={styles.logoContainer}>
                                <Image src={Logo} />
                            </View>
                            <View style={{ marginLeft: 10 }}>
                                <Text style={styles.addressText}>Plot 10111 off Mumbwa Road, Chinika Area</Text>
                                <Text style={styles.addressText}>P.O. Box 30203, Lusaka, Zambia.</Text>
                                <Text style={styles.addressText}>Mobile: +260 764 299 293</Text>
                                <Text style={styles.addressText}>Email: info@iagzambia.com</Text>
                            </View>
                        </View>

                        <View style={{ alignItems: 'flex-end', width: "50%" }}>
                            <Text style={styles.deliveryNoteBadge}>DELIVERY NOTE (CONTINUED)</Text>
                            <View style={{ marginTop: 5 }}>
                                <View style={styles.detailRow}>
                                    <Text style={styles.label}>D.N:</Text>
                                    <Text style={styles.value}>{pdf_data?.delivery_note?.delivery_note_ref_number || "---"}</Text>
                                </View>
                            </View>
                        </View>
                    </View>

                    {wb ? renderWeighbridgeTable() : null}
                    {renderSignatureSection()}
                </Page>
            )}
        </Document>
    );
};

export default DeliveryNotePDF;
